/**
 * Admin Utilities - Modular Firebase API
 *
 * Functions for admin and publisher operations using Firebase Cloud Functions.
 */

import { getFirebaseAuth } from "api/firebase/firebase";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import { getFirebaseApp } from "api/firebase/firebase";
import { debugTimeStart, debugTimeEnd } from "utils/utils";

// Cache the functions instance and emulator connection state
let functionsInstance = null;
let emulatorConnected = false;

/**
 * Gets the Firebase Functions instance with proper emulator configuration
 * @returns {Functions}
 */
function getFirebaseFunctions() {
  if (!functionsInstance) {
    functionsInstance = getFunctions(getFirebaseApp());
  }

  // Connect to emulator if needed (only once)
  if (!emulatorConnected) {
    if (process.env.REACT_APP_USE_RTD_EMULATOR === "true") {
      connectFunctionsEmulator(functionsInstance, "localhost", 5005);
      emulatorConnected = true;
    } else if (process.env.NODE_ENV === "development") {
      connectFunctionsEmulator(functionsInstance, "localhost", 5001);
      emulatorConnected = true;
    }
  }

  return functionsInstance;
}

/**
 * DRY Helper: callFirebaseFunction
 * - Minimizes repetitive code for each callable function.
 * - Logs and rethrows errors for consistent error handling.
 */
async function callFirebaseFunction(functionName, data = {}, options = {}) {
  debugTimeStart("callFirebaseFunction " + functionName);
  try {
    const functions = getFirebaseFunctions();
    const timeout = options.timeout || 60000;
    const callable = httpsCallable(functions, functionName, { timeout });
    const response = await callable(data);
    return response.data;
  } catch (error) {
    // `quiet` callers handle (or fail open on) the error themselves; logging
    // at error level would also send it to Sentry via the console integration.
    const log = options.quiet ? console.warn : console.error;
    log(`Error in callable "${functionName}":`, error);
    throw error;
  } finally {
    debugTimeEnd("callFirebaseFunction " + functionName);
  }
}

/**
 * Calls one of the FAO GeoID registry proxy functions (`registerGeoId`,
 * `fetchGeoId`).
 *
 * These exist only because the GeoID API sends no CORS headers, so the browser
 * cannot call it directly. They relay the registry's answer verbatim, hence the
 * `{status, body}` shape — `utils/GeoIdAPI` needs the status to tell a
 * duplicate-geometry 409 (which names a usable incumbent geoid) apart from a
 * real failure.
 *
 * @param {"registerGeoId"|"fetchGeoId"} functionName
 * @param {Object} data
 * @returns {Promise<{status: number, body: Object}>}
 */
export async function callGeoIdFunction(functionName, data = {}) {
  // 30s: the proxy caps the upstream call at 15s, so this only has to leave
  // room for the round trip without stranding a boundary save.
  return callFirebaseFunction(functionName, data, { timeout: 30000 });
}

// Reusable reference for customizationName
const CUSTOMIZATION_NAME =
  process.env.REACT_APP_CUSTOMIZATION_NAME || "default";

// Session cache for the admin/publisher permission checks. The Navbar, the
// protected routes, Admin.js, and every publisher operation all ask the same
// question, so the answer is memoized per user. The in-flight promise (not the
// resolved value) is stored so the concurrent burst at startup collapses into
// one backend call per check; a rejected call is evicted so errors are never
// cached. Keying by uid makes a user switch miss automatically, and the TTL
// picks up granted/revoked roles during long sessions. Client-side caching is
// UX-only: the backend re-validates permissions on every privileged callable.
const PERMISSION_CACHE_TTL_MS = 15 * 60 * 1000;
const permissionCache = new Map(); // "uid:functionName" -> {promise, expiresAt}

// The RAW response promise is cached (not a derived boolean) so several
// accessors reading different fields of the same callable — e.g. isPublisher
// and canViewStatistics from checkPublisher — share one network call.
function cachedPermissionCall(functionName) {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;

  const key = `${user.uid}:${functionName}`;
  const cached = permissionCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.promise;
  }

  const promise = callFirebaseFunction(functionName, {
    customizationName: CUSTOMIZATION_NAME,
  });
  permissionCache.set(key, {
    promise,
    expiresAt: Date.now() + PERMISSION_CACHE_TTL_MS,
  });
  promise.catch(() => {
    // Evict only our own entry — a newer retry may have replaced it.
    if (permissionCache.get(key)?.promise === promise) {
      permissionCache.delete(key);
    }
  });
  return promise;
}

function getCachedPermission(functionName, resultField) {
  const call = cachedPermissionCall(functionName);
  if (!call) return Promise.resolve(false);
  return call.then((data) => data?.[resultField] === true);
}

/**
 * Checks if the current user is an admin for the current customization.
 * Cached per user for the session (see permissionCache above).
 * @returns {Promise<boolean>} Resolves to true if the user is an admin, false otherwise.
 */
export async function getAdminStatus() {
  // Start timing getAdminStatus
  debugTimeStart("getAdminStatus");
  try {
    return await getCachedPermission("checkAdmin", "isAdmin");
  } finally {
    // End timing getAdminStatus
    debugTimeEnd("getAdminStatus");
  }
}

/**
 * Checks if the current user has publisher permissions for the current
 * customization. Cached per user for the session (see permissionCache above).
 * @returns {Promise<boolean>} Resolves to true if the user can publish, false otherwise.
 */
export async function getPublisherStatus() {
  debugTimeStart("getPublisherStatus");
  try {
    return await getCachedPermission("checkPublisher", "isPublisher");
  } finally {
    debugTimeEnd("getPublisherStatus");
  }
}

/**
 * Checks if the current user may view usage statistics: their publisher
 * entry must carry the opt-in `statistics: true` flag (enforced
 * server-side by getUsageStatistics as well). Shares checkPublisher's
 * cached call — no extra network request.
 * @returns {Promise<boolean>}
 */
export async function getStatisticsAccess() {
  debugTimeStart("getStatisticsAccess");
  try {
    return await getCachedPermission("checkPublisher", "canViewStatistics");
  } finally {
    debugTimeEnd("getStatisticsAccess");
  }
}

/**
 * Updates the customization configuration in the backend.
 * @param {Object} config The updated configuration
 * @returns {Promise<boolean>} Resolves to true if the update is successful
 */
export async function updateCustomizationConfig(config) {
  // Start timing updateCustomizationConfig
  debugTimeStart("updateCustomizationConfig");
  try {
    if (!config) {
      throw new Error("Invalid request: config is required.");
    }

    const data = await callFirebaseFunction("updateCustomizationConfig", {
      customizationName: CUSTOMIZATION_NAME,
      config,
    });
    return data?.success === true;
  } finally {
    // End timing updateCustomizationConfig
    debugTimeEnd("updateCustomizationConfig");
  }
}

/**
 * Function to update a boundary in the backend
 * @param {string} aoiId
 * @param {string} boundaryId
 * @param {Object} updatedBoundary
 * @returns {Promise<any>} - The response data from the backend
 */
export async function updateBoundary(aoiId, boundaryId, updatedBoundary) {
  // Start timing updateBoundary
  debugTimeStart("updateBoundary");
  try {
    return await callFirebaseFunction("updateBoundary", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      boundaryId,
      updatedBoundary,
    });
  } finally {
    // End timing updateBoundary
    debugTimeEnd("updateBoundary");
  }
}

/**
 * Function to download a GeoJSON boundary file from the backend
 * @param {string} filePath
 * @returns {Promise<Object>} - The GeoJSON content from the backend
 */
export async function downloadGeoJsonBoundary(filePath) {
  // Start timing downloadGeoJsonBoundary
  debugTimeStart("downloadGeoJsonBoundary");
  try {
    return await callFirebaseFunction("downloadBoundaryGeoJson", {
      customizationName: CUSTOMIZATION_NAME,
      filePath,
    });
  } finally {
    // End timing downloadGeoJsonBoundary
    debugTimeEnd("downloadGeoJsonBoundary");
  }
}

/**
 * Function to update a GeoJSON boundary file in the backend
 * @param {string} filePath - Desired storage path.
 * @param {Object} geoJsonContent
 * @param {Object} [options]
 * @param {boolean} [options.avoidCollision] - If true and filePath already exists, the backend appends `_2`, `_3`, ... before the extension.
 * @param {string|null} [options.oldFilePath] - If provided and different from the final path, the backend deletes this file after saving.
 * @returns {Promise<any>} - The response data from the backend, including `filePath` (final path) and `geojsonSrc` (signed URL).
 */
export async function updateGeoJsonBoundary(
  filePath,
  geoJsonContent,
  { avoidCollision = false, oldFilePath = null } = {},
) {
  // Start timing updateGeoJsonBoundary
  debugTimeStart("updateGeoJsonBoundary");
  try {
    return await callFirebaseFunction("updateBoundaryGeoJson", {
      customizationName: CUSTOMIZATION_NAME,
      filePath,
      geoJsonContent,
      avoidCollision,
      oldFilePath,
    });
  } finally {
    // End timing updateGeoJsonBoundary
    debugTimeEnd("updateGeoJsonBoundary");
  }
}

/**
 * Function to delete a boundary in the backend
 * @param {string} aoiId
 * @param {string} boundaryId
 * @param {string} geojsonFilePath
 * @returns {Promise<void>}
 */
export async function deleteBoundary(aoiId, boundaryId, geojsonFilePath) {
  // Start timing deleteBoundary
  debugTimeStart("deleteBoundary");
  try {
    const data = await callFirebaseFunction("removeBoundary", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      boundaryId,
      geojsonFilePath,
    });
    if (!data?.success) {
      throw new Error(
        `Failed to delete boundary: ${data?.message || "Unknown error"}`,
      );
    }
  } finally {
    // End timing deleteBoundary
    debugTimeEnd("deleteBoundary");
  }
}

/**
 * Function to upload an image to Firebase Storage using the backend function.
 * @param {File} imageFile - The image file to upload.
 * @param {string} filePath - Desired path for storing the image in Firebase Storage.
 * @returns {Promise<string>} - The public URL of the uploaded image.
 */
export async function uploadImage(imageFile, filePath) {
  // Start timing uploadImage
  debugTimeStart("uploadImage");
  try {
    if (!imageFile) throw new Error("No image file provided.");

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);

      reader.onload = async () => {
        try {
          const base64Image = reader.result.split(",")[1];
          const data = await callFirebaseFunction("uploadImage", {
            customizationName: CUSTOMIZATION_NAME,
            filePath,
            imageBuffer: base64Image,
            mimeType: imageFile.type,
          });
          resolve(data?.imageUrl);
        } catch (err) {
          console.error("Error uploading image:", err);
          reject(err);
        }
      };

      reader.onerror = (err) => {
        console.error("Error reading image file:", err);
        reject(err);
      };
    });
  } finally {
    // End timing uploadImage
    debugTimeEnd("uploadImage");
  }
}

/**
 * Function to add a layer to an AOI
 * @param {string} aoiId
 * @param {string} layerId
 * @returns {Promise<any>}
 */
export async function addLayerToArea(aoiId, layerId) {
  // Start timing addLayerToArea
  debugTimeStart("addLayerToArea");
  try {
    return await callFirebaseFunction("addLayerToArea", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      layerId,
    });
  } finally {
    // End timing addLayerToArea
    debugTimeEnd("addLayerToArea");
  }
}

/**
 * Function to remove a layer from an AOI
 * @param {string} aoiId
 * @param {string} layerId
 * @returns {Promise<any>}
 */
export async function removeLayerFromArea(aoiId, layerId) {
  // Start timing removeLayerFromArea
  debugTimeStart("removeLayerFromArea");
  try {
    return await callFirebaseFunction("removeLayerFromArea", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      layerId,
    });
  } finally {
    // End timing removeLayerFromArea
    debugTimeEnd("removeLayerFromArea");
  }
}

/**
 * Function to add a script to an AOI
 * @param {string} aoiId
 * @param {string} scriptId
 * @returns {Promise<any>}
 */
export async function addScriptToArea(aoiId, scriptId) {
  // Start timing addScriptToArea
  debugTimeStart("addScriptToArea");
  try {
    return await callFirebaseFunction("addScriptToArea", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      scriptId,
    });
  } finally {
    // End timing addScriptToArea
    debugTimeEnd("addScriptToArea");
  }
}

/**
 * Function to remove a script from an AOI
 * @param {string} aoiId
 * @param {string} scriptId
 * @returns {Promise<any>}
 */
export async function removeScriptFromArea(aoiId, scriptId) {
  // Start timing removeScriptFromArea
  debugTimeStart("removeScriptFromArea");
  try {
    return await callFirebaseFunction("removeScriptFromArea", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      scriptId,
    });
  } finally {
    // End timing removeScriptFromArea
    debugTimeEnd("removeScriptFromArea");
  }
}

/**
 * Fetch info about a GEE asset
 * @param {string} geeAssetLink
 * @returns {Promise<Object>}
 */
export async function getGEEAssetInfo(geeAssetLink) {
  // Start timing getGEEAssetInfo
  debugTimeStart("getGEEAssetInfo");
  try {
    return await callFirebaseFunction("getGEEAssetInfo", {
      customizationName: CUSTOMIZATION_NAME,
      geeAssetLink,
    });
  } finally {
    // End timing getGEEAssetInfo
    debugTimeEnd("getGEEAssetInfo");
  }
}

/**
 * Check whether Earth Engine can read a Cloud Storage source.
 *
 * Non-throwing on the backend: returns a report the UI can render inline
 * rather than an exception, so the layer form stays open.
 *
 * @param {string} uri gs:// or storage.*.com URL
 * @returns {Promise<Object>} { readable, httpStatus, bucket, object,
 *   serviceAccounts, message, metadata }
 */
export async function checkCloudSourceAccess(uri) {
  debugTimeStart("checkCloudSourceAccess");
  try {
    const response = await callFirebaseFunction(
      "checkCloudSourceAccess",
      { customizationName: CUSTOMIZATION_NAME, uri },
      // A cold Earth Engine auth plus a Cloud Storage round trip can exceed
      // the 60s default.
      { timeout: 120000 },
    );
    return response?.data || null;
  } finally {
    debugTimeEnd("checkCloudSourceAccess");
  }
}

/**
 * Fetch the image property names of a layer's ImageCollection.
 * @param {string} srcImageCollection
 * @returns {Promise<string[]>} Array of property names (system:* filtered, sorted)
 */
// Session cache: a collection's property names are static for practical
// purposes, and two form controls (the facets editor and the year-property
// field) ask for the same collection — share one in-flight/settled call.
const collectionPropertiesCache = new Map(); // src -> Promise<string[]>

export async function getImageCollectionProperties(srcImageCollection) {
  // Start timing getImageCollectionProperties
  debugTimeStart("getImageCollectionProperties");
  try {
    if (!collectionPropertiesCache.has(srcImageCollection)) {
      const promise = callFirebaseFunction("getImageCollectionProperties", {
        customizationName: CUSTOMIZATION_NAME,
        srcImageCollection,
        // backend returns { success, data: [...names] }; callFirebaseFunction
        // already unwraps the callable's .data envelope, so `response` IS
        // { success, data }.
      }).then((response) => response?.data || []);
      collectionPropertiesCache.set(srcImageCollection, promise);
      promise.catch(() => {
        // Never cache a failure — the next call retries.
        if (collectionPropertiesCache.get(srcImageCollection) === promise) {
          collectionPropertiesCache.delete(srcImageCollection);
        }
      });
    }
    return await collectionPropertiesCache.get(srcImageCollection);
  } finally {
    // End timing getImageCollectionProperties
    debugTimeEnd("getImageCollectionProperties");
  }
}

/**
 * Fetch the DISTINCT values of a given image property across a layer's
 * ImageCollection. Used to auto-fill an image facet's allowed-values list.
 * @param {string} srcImageCollection
 * @param {string} property
 * @returns {Promise<Array>} Array of distinct values (numbers stay numbers, sorted)
 */
export async function getImageCollectionPropertyValues(
  srcImageCollection,
  property,
) {
  // Start timing getImageCollectionPropertyValues
  debugTimeStart("getImageCollectionPropertyValues");
  try {
    const response = await callFirebaseFunction(
      "getImageCollectionPropertyValues",
      {
        customizationName: CUSTOMIZATION_NAME,
        srcImageCollection,
        property,
      },
    );
    // backend returns { success, data: [...distinctValues] }; callFirebaseFunction
    // already unwraps the callable's .data envelope, so `response` IS
    // { success, data }.
    return response?.data || [];
  } finally {
    // End timing getImageCollectionPropertyValues
    debugTimeEnd("getImageCollectionPropertyValues");
  }
}

/**
 * Fetches GEE asset info for authenticated (non-admin) users.
 * Used for Sepal asset imports.
 * @param {string} geeAssetLink
 * @returns {Promise<Object>}
 */
export async function getGEEAssetInfoAuth(geeAssetLink) {
  debugTimeStart("getGEEAssetInfoAuth");
  try {
    return await callFirebaseFunction("getGEEAssetInfoAuth", {
      customizationName: CUSTOMIZATION_NAME,
      geeAssetLink,
    });
  } finally {
    debugTimeEnd("getGEEAssetInfoAuth");
  }
}

/**
 * Add a layer to the DB
 * @param {string} layerId
 * @param {Object} layerData
 * @returns {Promise<any>}
 */
export async function addLayerToDb(layerId, layerData) {
  // Start timing addLayerToDb
  debugTimeStart("addLayerToDb");
  try {
    return await callFirebaseFunction("addLayer", {
      customizationName: CUSTOMIZATION_NAME,
      layerId,
      layerData,
    });
  } finally {
    // End timing addLayerToDb
    debugTimeEnd("addLayerToDb");
  }
}

/**
 * Get all areas with a given layerId
 * @param {string} layerId
 * @returns {Promise<any>}
 */
export async function getAreasWithLayerId(layerId) {
  // Start timing getAreasWithLayerId
  debugTimeStart("getAreasWithLayerId");
  try {
    return await callFirebaseFunction("getAreasWithLayerId", {
      customizationName: CUSTOMIZATION_NAME,
      layerId,
    });
  } finally {
    // End timing getAreasWithLayerId
    debugTimeEnd("getAreasWithLayerId");
  }
}

/**
 * Remove a layer from the DB
 * @param {string} layerId
 * @returns {Promise<any>}
 */
export async function removeLayerFromDb(layerId) {
  // Start timing removeLayerFromDb
  debugTimeStart("removeLayerFromDb");
  try {
    return await callFirebaseFunction("removeLayer", {
      customizationName: CUSTOMIZATION_NAME,
      layerId,
    });
  } finally {
    // End timing removeLayerFromDb
    debugTimeEnd("removeLayerFromDb");
  }
}

/**
 * Create a new layer group owned by the current customization.
 * The backend forces `ownedBy` server-side regardless of what the client sends.
 * @param {string} groupId
 * @param {Object} groupData - { name, icon?, iconImage?, position?, backgroundColor?, header? }
 * @returns {Promise<any>}
 */
export async function addLayerGroup(groupId, groupData) {
  debugTimeStart("addLayerGroup");
  try {
    return await callFirebaseFunction("addLayerGroup", {
      customizationName: CUSTOMIZATION_NAME,
      groupId,
      groupData,
    });
  } finally {
    debugTimeEnd("addLayerGroup");
  }
}

/**
 * Update a layer group. The backend refuses if the group is not owned by
 * the current customization.
 * @param {string} groupId
 * @param {Object} groupData
 * @returns {Promise<any>}
 */
export async function updateLayerGroup(groupId, groupData) {
  debugTimeStart("updateLayerGroup");
  try {
    return await callFirebaseFunction("updateLayerGroup", {
      customizationName: CUSTOMIZATION_NAME,
      groupId,
      groupData,
    });
  } finally {
    debugTimeEnd("updateLayerGroup");
  }
}

/**
 * Delete a layer group. The backend refuses if the group is still used by
 * any layer and returns { code: 'group-in-use', blockingLayers: [...] }.
 * @param {string} groupId
 * @returns {Promise<any>}
 */
export async function removeLayerGroup(groupId) {
  debugTimeStart("removeLayerGroup");
  try {
    return await callFirebaseFunction("removeLayerGroup", {
      customizationName: CUSTOMIZATION_NAME,
      groupId,
    });
  } finally {
    debugTimeEnd("removeLayerGroup");
  }
}

/**
 * Function to update the multilingual name of an AOI
 * @param {string} aoiId
 * @param {Object} newNameObj - e.g., { en: "Brazil", fr: "Brésil" }
 */
export async function updateAoiName(aoiId, newNameObj) {
  debugTimeStart("updateAoiName");
  try {
    return await callFirebaseFunction("updateAoiName", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      name: newNameObj,
    });
  } finally {
    debugTimeEnd("updateAoiName");
  }
}

/**
 * Function to update the external platform link of an AOI
 * @param {string} aoiId
 * @param {string} link - the URL string or empty string to delete
 * @returns {Promise<any>}
 */
export async function updateExternalPlatformLink(aoiId, link) {
  debugTimeStart("updateExternalPlatformLink");
  try {
    return await callFirebaseFunction("updateExternalPlatformLink", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      link,
    });
  } finally {
    debugTimeEnd("updateExternalPlatformLink");
  }
}

/**
 * Updates the WHISP national ISO2 code of an AOI (e.g. "br"), or removes it.
 * @param {string} aoiId
 * @param {string|null} code - 2-letter ISO2 code, or null/empty to remove.
 * @returns {Promise<any>}
 */
export async function updateNationalCode(aoiId, code) {
  debugTimeStart("updateNationalCode");
  try {
    return await callFirebaseFunction("updateNationalCode", {
      customizationName: CUSTOMIZATION_NAME,
      aoiId,
      code,
    });
  } finally {
    debugTimeEnd("updateNationalCode");
  }
}

/**
 * Checks if maintenance mode is enabled and if the current user should be blocked
 * @returns {Promise<Object>} Object with isEnabled and shouldBlock properties
 */
export async function checkMaintenanceMode() {
  debugTimeStart("checkMaintenanceMode");
  try {
    return await callFirebaseFunction(
      "checkMaintenanceMode",
      { customizationName: CUSTOMIZATION_NAME },
      // Non-critical: App fails open when this cannot be reached.
      { quiet: true },
    );
  } finally {
    debugTimeEnd("checkMaintenanceMode");
  }
}

/**
 * Updates the global maintenance mode setting in the realtime database.
 * Only admins of the platform (earthmap) customization may change it. The
 * admin switch "disables site access", so enabling it also blocks: the
 * maintenance dialog cannot be closed.
 * @param {boolean} enabled - Whether maintenance mode should be enabled
 * @returns {Promise<Object>} Response object with success status
 */
export async function updateMaintenanceMode(enabled) {
  debugTimeStart("updateMaintenanceMode");
  try {
    return await callFirebaseFunction("updateMaintenanceMode", {
      customizationName: CUSTOMIZATION_NAME,
      maintenanceMode: { isEnabled: !!enabled, shouldBlock: !!enabled },
    });
  } finally {
    debugTimeEnd("updateMaintenanceMode");
  }
}

/**
 * Checks what has changed in development compared to production
 * @returns {Promise<Object>} Response with changes to be published
 */
export async function checkChangesToPublish() {
  debugTimeStart("checkChangesToPublish");
  try {
    // Check publisher permissions first
    const isPublisher = await getPublisherStatus();
    if (!isPublisher) {
      throw new Error("Insufficient permissions. Publisher role required.");
    }

    return await callFirebaseFunction("checkChangesToPublish", {
      customizationName: CUSTOMIZATION_NAME,
    });
  } finally {
    debugTimeEnd("checkChangesToPublish");
  }
}

/**
 * Fetches Earth Map usage statistics for the publisher dashboard.
 * Backed by the `getUsageStatistics` callable, which reads the monthly
 * per-caller rollups of the REST API (Firestore `apiUsage` collection).
 * @param {number} months - How many trailing months to fetch (default 6)
 * @returns {Promise<Object>} { success, months: ["YYYY-MM", ...], rows: [...] }
 */
export async function getUsageStatistics(months = 6) {
  debugTimeStart("getUsageStatistics");
  try {
    // Check publisher permissions first
    const isPublisher = await getPublisherStatus();
    if (!isPublisher) {
      throw new Error("Insufficient permissions. Publisher role required.");
    }

    return await callFirebaseFunction("getUsageStatistics", {
      customizationName: CUSTOMIZATION_NAME,
      months,
    });
  } finally {
    debugTimeEnd("getUsageStatistics");
  }
}

/**
 * Promotes approved changes to production
 * @param {Object} changesToPromote - The changes to promote (same format as checkChangesToPublish response)
 * @returns {Promise<Object>} Response object with success status
 */
export async function promoteToProduction(changesToPromote) {
  debugTimeStart("promoteToProduction");
  try {
    // Check publisher permissions first
    const isPublisher = await getPublisherStatus();
    if (!isPublisher) {
      throw new Error("Insufficient permissions. Publisher role required.");
    }

    return await callFirebaseFunction("promoteToProduction", {
      customizationName: CUSTOMIZATION_NAME,
      changes: changesToPromote,
    });
  } finally {
    debugTimeEnd("promoteToProduction");
  }
}

/**
 * Translates text from a source language to target languages using AI
 * @param {string} text - The text to translate
 * @param {string} sourceLanguage - The source language code (e.g., 'en', 'es')
 * @param {Array<string>} targetLanguages - Array of target language codes
 * @returns {Promise<Object>} Object with translations keyed by language code
 */
export async function translateText(text, sourceLanguage, targetLanguages) {
  debugTimeStart("translateText");
  try {
    return await callFirebaseFunction("translateText", {
      customizationName: CUSTOMIZATION_NAME,
      text,
      sourceLanguage,
      targetLanguages,
    });
  } finally {
    debugTimeEnd("translateText");
  }
}

/**
 * Generate an AI-powered Markdown description for a layer
 * @param {Object} layerInfo - Layer metadata (name, dataSrc, units, classes, etc.)
 * @param {Array<string>} languages - Target language codes
 * @returns {Promise<Object>} Object with descriptions keyed by language code
 */
export async function generateLayerDescription(layerInfo, languages) {
  debugTimeStart("generateLayerDescription");
  try {
    return await callFirebaseFunction(
      "generateLayerDescription",
      {
        customizationName: CUSTOMIZATION_NAME,
        layerInfo,
        languages,
      },
      {
        timeout: 120000,
      },
    );
  } finally {
    debugTimeEnd("generateLayerDescription");
  }
}

/**
 * Generate layer metadata from GEE catalogs and web using AI
 * @param {string} assetId - GEE asset ID
 * @param {Object} geeData - Data from getGEEAssetInfo
 * @param {string|null} selectedBand - Currently selected band name
 * @param {Object} layerGroups - Available layer groups
 * @param {Array<string>} languages - Target language codes
 * @returns {Promise<Object>} Structured metadata for the layer
 */
export async function generateLayerMetadata(
  assetId,
  geeData,
  selectedBand,
  layerGroups,
  existingClassNames,
  referenceUrl,
  languages,
) {
  debugTimeStart("generateLayerMetadata");
  try {
    return await callFirebaseFunction(
      "generateLayerMetadata",
      {
        customizationName: CUSTOMIZATION_NAME,
        assetId,
        geeData,
        selectedBand,
        layerGroups,
        existingClassNames,
        referenceUrl,
        languages,
      },
      {
        timeout: 120000,
      },
    );
  } finally {
    debugTimeEnd("generateLayerMetadata");
  }
}

/**
 * Generate metadata for a WMS / WMTS / XYZ tile layer.
 * @param {Object} layerInfo - Service + GetCapabilities-derived + current admin values
 * @param {Array<string>} fieldsToFill - Subset of ["name","description","dataSrcName","attribution","group"]
 * @param {Object|null} layerGroups - Available layer groups, required if "group" is requested
 * @param {Array<string>} languages - Target language codes
 * @returns {Promise<Object>} Requested fields only. Multilingual for name/description.
 */
export async function generateWebTileLayerMetadata(
  layerInfo,
  fieldsToFill,
  layerGroups,
  languages,
) {
  debugTimeStart("generateWebTileLayerMetadata");
  try {
    return await callFirebaseFunction(
      "generateWebTileLayerMetadata",
      {
        customizationName: CUSTOMIZATION_NAME,
        layerInfo,
        fieldsToFill,
        layerGroups,
        languages,
      },
      {
        timeout: 120000,
      },
    );
  } finally {
    debugTimeEnd("generateWebTileLayerMetadata");
  }
}
